/**
 * Cantidades y dosis tecleadas a mano: lo que se escribe se guarda como TEXTO y
 * solo se convierte a número para avisar al formulario.
 *
 * POR QUÉ (auditoría del portal del perito, C2 · T02-01, 26-09-2026). Los campos
 * de dosis eran `type="number"` controlados con `value={amount === 0 ? "" : amount}`
 * y un `parseFloat` en cada tecla. Al teclear «0,05»:
 *   «0»    → 0 → el campo se pinta vacío (0 se enseñaba como "")
 *   «0,»   → el navegador lo da por inválido → "" → 0 → vacío
 *   «0,0»  → 0 → vacío
 *   «0,05» → lo que queda en el campo es «5» → 5
 * La receta se guardaba cien veces más fuerte, sin ningún aviso: Spintor a
 * 0,025 L/1000 m² quedó a 25 y la tarea pidió 43,45 L para 0,17 ha. Le pasaba
 * igual al admin, en escritorio, en el móvil y en Safari.
 *
 * Ahora el campo es de texto con teclado decimal (`inputMode="decimal"`), lo
 * tecleado se respeta tal cual (con coma o con punto) y el número sale de aquí.
 */

/** Lo tecleado → número, o `null` si todavía no es un número («», «,», «.»). */
export function parseDecimalInput(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).replace(/\s/g, "").replace(",", ".");
  if (s === "" || s === ".") return null;
  if (!/^\d*\.?\d*$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Lo que se deja escribir: cifras y UN separador decimal (coma o punto, el que
 * ponga la persona). Sin signo: una dosis negativa no existe.
 */
export function sanitizeDecimalTyping(raw: string): string {
  let out = "";
  let sep = false;
  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") {
      out += ch;
    } else if ((ch === "," || ch === ".") && !sep) {
      out += ch;
      sep = true;
    }
  }
  return out;
}

/** ¿Este idioma escribe los decimales con coma? (es, fr, it, pt… sí; en, zh no). */
export function usesDecimalComma(locale?: string): boolean {
  try {
    return new Intl.NumberFormat(locale || undefined).format(1.5).includes(",");
  } catch {
    return false;
  }
}

/**
 * Un número guardado → el texto con el que se abre el campo. 0 o vacío → "" (se
 * ve el marcador). Hasta 6 decimales, sin separador de millares, con la coma del
 * idioma si la usa: «0,025», no «0.025» ni «0,03».
 */
export function formatDecimalForInput(n: number | null | undefined, locale?: string): string {
  if (n == null || !Number.isFinite(n) || n === 0) return "";
  const s = String(Number(n.toFixed(6)));
  return usesDecimalComma(locale) ? s.replace(".", ",") : s;
}

/**
 * Una dosis para LEER (no para editar): hasta 4 decimales. `formatStock` corta a
 * 2 y una dosis de 0,025 salía como «0,03».
 */
export function formatDose(n: number | null | undefined, locale?: string): string {
  if (n == null || !Number.isFinite(n)) return "0";
  return n.toLocaleString(locale || undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}
